/** @module csi/config/preferences */
define([
	"underscore",
	"csi",
	"csi/core/extendedPathService",
	"csi/core/PathHints",
	"csi/locale",
	"csi/reflection",
	"csi/xml"
], function(_, csi, {parseExtendedPath, parsePathSteps}, PathHints, locale, reflection, xml) {

	const _pathHints = PathHints.globalHints;

	var _userDefaults;
	var _hiddenSearchTypes = {};
	var _hiddenViews = {};
	var _readOnlyViews = {};
	var _contextualPathHints = {};
	var _userDefaultValues;

	/**
	 * Initialize site and role settings for search types, views, and paths.
	 * @param {Node} sitePreferences - node of type SitePreferences storing site-wide settings ("centric://REFLECTION/INSTANCE/SitePreferences/null")
	 * @param {Node} [rolePreferences] - node of type SitePreferences owned by role being played by user
	 */
	function init(sitePreferences, rolePreferences) {
		_hiddenSearchTypes = {};
		_hiddenViews = {};
		_readOnlyViews = {};
		_userDefaults = {};

		_pathHints.addHiddenPathsFromPreferences(sitePreferences);
		csi.hashArray(sitePreferences.HiddenSearchTypes, null, Boolean, _hiddenSearchTypes);
		csi.hashArray(sitePreferences.HiddenViews, null, Boolean, _hiddenViews);
		csi.hashArray(sitePreferences.ReadOnlyViews, null, Boolean, _readOnlyViews);
		csi.hashArray(sitePreferences.LicenseRestrictedViews, null, Boolean, _hiddenViews);

		if (rolePreferences) {
			_pathHints.addHiddenPathsFromPreferences(rolePreferences);
			csi.hashArray(rolePreferences.HiddenSearchTypes, null, Boolean, _hiddenSearchTypes);
			csi.hashArray(rolePreferences.HiddenViews, null, Boolean, _hiddenViews);
			csi.hashArray(rolePreferences.ReadOnlyViews, null, Boolean, _readOnlyViews);
		}
	}

	/**
	 * Add a user default that will help automatically populate defaults to forms, and pre-filter selection and view queries.
	 * Return true if default has unique name for given target type and was added to system. 
	 * @param {UserDefault} ud
	 * @return {boolean}
	 */
	function addUserDefault(ud) {
		if (_.findWhere(getUserDefaultsForType(ud.targetType), {name: ud.name})) {
			return false;
		}

		if (_userDefaultValues) {
			ud.value = _userDefaultValues[ud.userAttributeId];
		}

		csi.objectArrayAddValue(_userDefaults, ud.targetType, ud);
		return true;
	}

	/**
	 * A function returning true or false indicating if the User Default is applicable
	 * @callback userDefaultFilterCallback
	 * @param {UserDefault} ud
	 * @param {ExtendedPath} [matrixExtendedPath]
	 * @return {boolean}
	 */

	/**
	 * Apply user defaults to query for given node type that pass an optional filter condition; if applying defaults
	 * to a matrix, then pass the extended path to reach the given node type
	 * @param {Query} q
	 * @param {string} type
	 * @param {ExtendedPath} [matrixExtendedPath]
	 * @param {userDefaultFilterCallback} [filterFunction]
	 */
	function applyUserDefaultsToQuery(q, type, matrixExtendedPath, filterFunction) {
		var uds = getUserDefaultsForType(type, true);
		if (uds) {
			_.each(uds, function(ud) {
				if (!filterFunction || filterFunction(ud, matrixExtendedPath)) {
					ud.toQuery(q, matrixExtendedPath);
				}
			});
		}
	}

	/**
	 * Return defaults for given target attribute; if user defaults are provided, then also test to see if user attribute has non-default value
	 * @param {string} targetType
	 * @param {string} targetAttributeId
	 * @param {string} [targetPath]
	 * @return {UserDefault | undefined}
	 */
	function getUserDefaultForAttribute(targetType, targetAttributeId, targetPath) {
		if (!_userDefaultValues) {
			return;
		}

		var ud = _.findWhere(_userDefaults[targetType], {
			targetAttributeId,
			targetPath: targetPath ?? "",
		});

		// do not apply default is user attribute value is empty (only applies for enum and enumlist types - boolean values always apply)
		if (ud && !ud.hasValidValue()) {
			return;
		}

		return ud;
	}

	/**
	 * Return defaults for given target type; if user defaults are provided, then filter list by those with non-default values
	 * @param {string} targetType
	 * @param {boolean} bForFiltering - if defaults will be used for filtering, then remove superseded filters
	 * @return {UserDefault[] | undefined}
	 */
	function getUserDefaultsForType(targetType, bForFiltering) {
		if (!_userDefaultValues) {
			return;
		}

		var uds = _userDefaults[targetType];
		if (uds) {
			uds = _.filter(uds, function(ud) {
				return ud.hasValidValue();
			});

			if (!uds.length) {
				return;
			}

			if (bForFiltering) {
				uds = removeSupersededUserDefaults(uds);
			}
		}

		return uds;
	}

	/**
	 * Remove defaults that are superseded by others in the list
	 * @param {UserDefault[]} uds
	 * @return {UserDefault[]}
	 */
	function removeSupersededUserDefaults(uds) {
		if (uds.length <= 1) {
			return uds;
		}

		const hash = csi.hashArray(uds, "name", Boolean);

		return uds.filter((ud) => ud.filterSupersededBy.every((name) => !hash[name]));
	}

	/**
	 * Apply user default values to user defaults
	 * @param {Partial<C8Node> | undefined} values
	 */
	function setUserDefaultValues(values) {
		_userDefaultValues = values;

		_.each(_.flatten(_.values(_userDefaults)), function(ud) {
			ud.value = _userDefaultValues ? _userDefaultValues[ud.userAttributeId] : undefined;
		});
	}

	/**
	 * Add path hints that are only true under certain conditions, identified by given contextId
	 * @param {string|null} contextId - unique identifier; if not provided, then hints will not be cached
	 * @param {string[]} typedPaths - typed paths
	 * @param {string[]} omitPaths - typed omit paths
	 * @return {PathHints}
	 */
	function addContextualPathHints(contextId, typedPaths, omitPaths) {
		var hints = _contextualPathHints[contextId];
		if (!hints) {
			hints = new PathHints(_pathHints, contextId);

			if (contextId) {
				_contextualPathHints[contextId] = hints;
			}
		}

		_.each(typedPaths, function(path) {
			hints.add(parseExtendedPath(path));
		});

		_.each(omitPaths, function(path) {
			hints.addHiddenPath(parseExtendedPath(path));
		});

		return hints;
	}

	/**
	 * Add path that should not be used in custom views, normally because there are quicker ways to get to the same information
	 * @param {ExtendedPath} ep
	 */
	function addHiddenPath(ep) {
		_pathHints.addHiddenPath(ep);
	}

	/**
	 * Add path hints that are always valid paths through the model
	 * @param {string[]} typedPaths - typed paths
	 */
	function addPathHints(typedPaths) {
		_.each(typedPaths, function(path) {
			// path hints are already typed, so can ignore validation of types
			_pathHints.add(parseExtendedPath(path, null, true));
		});
	}

	/**	
	  * Note : This function is used from the ReflectionModel file. 
	  * Add all path hints that are always valid paths through the model
	  * 
	  * @param {string[]} globalHints - typed global hints paths
	  * @param {string[]} globalOmits - typed global omits paths
	  * @param {[contextId: string, hints: string[], omits: string[]]} contextualHintsOmits
	  */
	function addAllPathHints(globalHints, globalOmits, ...contextualHintsOmits) {
		// add all global hints
		_.each(globalHints, function(path) {
			// path hints are already typed, so can ignore validation of types
			_pathHints.add(parseExtendedPath(path, null, true));
		});
		// add all global omits
		_.each(globalOmits, function(path) {
			_pathHints.addHiddenPath(parseExtendedPath(path, null, true));
		});
		// add all contextual hints and omits
		_.each(contextualHintsOmits, function(contextualTypedPathArray) {
			// add contextual Path hints and omits with contextId
			addContextualPathHints(contextualTypedPathArray[0], contextualTypedPathArray[1], contextualTypedPathArray[2]);
		});
	}

	/**
	 * Create a hidden paths object to configure for a view including the global defaults
	 * TODO: Possibly merge with getContextualPathHints - not sure yet
	 * @return {PathHints}
	 */
	function createHiddenPathsForView() {
		return new PathHints(_pathHints);
	}

	/**
	 * Return path hints for given context
	 * @return {string[]}
	 */
	function getAllContextualPathHintsIds() {
		return Object.keys(_contextualPathHints);
	}

	/**
	 * Return path hints for given context
	 * @param {string} contextId - unique identifier
	 * @return {PathHints}
	 */
	function getContextualPathHints(contextId) {
		return _contextualPathHints[contextId];
	}

	/**
	 * Return path hints for given context if specified; otherwise return global path hints in system
	 * @param {string} [contextId] - unique identifier for context
	 * @return {PathHints}
	 */
	function getPathHints(contextId) {
		return (contextId && getContextualPathHints(contextId)) || _pathHints;
	}

	/**
	 * Return name of attribute along path.
	 * @param {string} id - attribute id
	 * @param {ExtendedPath} ep - path to attribute
	 * @param {LocaleResolver} [lr] - resolver to use for localization resources (defaults to current user's locale)
	 * @return {string}
	 */
	function getAttributeNameAlongPath(id, ep, lr) {
		lr = lr || locale.localeResolver;
		return lr.getTypeAttributeAlongPath(ep.getEndType(), id, getPathName(ep, lr));
	}

	/**
	 * If there is at least one custom reference in the path, then return its name to act as a default custom path name.
	 * If there are multiple custom relationships in the path, then the last one is used.
	 * @param {ExtendedPath} ep
	 * @param {LocaleResolver} [lr] - resolver to use for localization resources (defaults to current user's locale)
	 * @return {string|undefined}
	 */
	function getCustomPathName(ep, lr) {
		var n = ep.depth;
		var rcr;

		if (!n) {
			return null;
		}

		lr = lr || locale.localeResolver;

		for (var i = n - 1; i >= 0; --i) {
			rcr = ep.getStepRelation(i);

			if (rcr && rcr.isCustom) {
				return lr.getTypeAttribute(ep.getStartTypeFromStep(i), rcr.getId());
			}
		}

		return null;
	}

	/**
	 * Return name of path, if any. The path could be gotten indirectly from just part of the path.
	 * @param {ExtendedPath} extendedPath
	 * @param {LocaleResolver} [lr] - resolver to use for localization resources (defaults to current user's locale)
	 * @return {string|undefined}
	 */
	function getPathName(extendedPath, lr) {
		const ep = extendedPath.clone();
		const names = [];
		let name;
		let path;
		let type;
		let steps;

		lr = lr || locale.localeResolver;

		while (ep.depth) {
			// need to handle both domain classes and abstract reflection classes
			type = ep.startFromDomainType() || ep.getStartType();
			path = ep.getStandardPath();
			name = null;

			// iterate until name is found or path is empty
			while (path) {
				name = lr.getTypePath(type, path);

				// name could be empty which means an OOTB path could have been overridden
				if (name != null) {
					break;
				}

				path = xml.pathRemoveStep(path);
			}

			if (name) {
				// if found, then store name for full or part of path, and keep iterating if there is still path steps remaining
				names.push(name);

				// determine number of steps used to name path
				steps = parsePathSteps(path);

				// remove named steps from extended path and then reset extended path to start from domain; in doing so, it is possible
				// that the path is right back where it started if named path was for a secondary relationship
				// Example:
				//   ep = "(BPMarketProductPlan)Child:Committed(BPCommittedDetails)/Child:SupplierItem(SupplierItem)"
				//   path = "Child:Committed"
				path = ep.getStandardPath();
				ep.shiftSteps(steps.length, true);
				ep.startFromDomainType();

				// if path is same as before, then shift to next domain; we do not configure path names from secondary classes, so no path
				// names will be missed
				if (path == ep.getStandardPath()) {
					ep.shiftDomain(true);
				}
			} else {
				// if not found, then remove first part of path and continue looking; but first, check to see if first relation in
				// path has implicit name (if relation targets same class as another relation on the owner class)
				const firstStepPath = ep.shiftDomain(name != null);

				if (firstStepPath?.isSingle) {
					const dc = reflection.getDomainClass(type);
					if (dc?.hasDuplicateDestination(firstStepPath.getEndType())) {
						// the path from shiftDomain will only have 1 step
						name = lr.getTypeAttribute(firstStepPath.getStartType(), firstStepPath.endRelationId);
						names.push(name);
					}
				}
			}
		}

		// combine names found along path together
		if (names.length) {
			return names.join(" ").trim();
		}

		// if no path name, then look to see if the end relation of the path is a custom relation - if so, use its name
		const {endRelation, previousType} = extendedPath;

		if (endRelation?.custom) {
			return lr.getTypeAttribute(previousType, endRelation.attributeId);
		}
	}

	/**
	 * Determine if given path can be traversed according to stored preferences.
	 * @param {ExtendedPath} ep - Extended path
	 * @return {boolean} true if path has been configured as hidden
	 */
	function isPathHidden(ep) {
		return _pathHints.isPathHidden(ep);
	}

	/**
	 * Return true if name of path stored explicitly for whole path.
	 * @param {ExtendedPath} ep
	 * @param {LocaleResolver} [lr] - resolver to use for localization resources (defaults to current user's locale)
	 * @return {boolean}
	 */
	function isPathNameExplicit(ep, lr) {
		lr = lr || locale.localeResolver;
		return !!lr.getTypePath(ep.getStartType(), ep.getStandardPath());
	}

	/**
	 * Determine if type should not be available for global searches.
	 * @param {string} type
	 * @return {boolean} true if type is hidden
	 */
	function isSearchTypeHidden(type) {
		return !!_hiddenSearchTypes[type];
	}

	/**
	 * Determine if view has been hidden in view configuration.
	 * @param {String} viewKey - key of view in form of "<View Id>|<Node Type>"
	 * @return {boolean} true if view is hidden
	 */
	function isViewHidden(viewKey) {
		return !!_hiddenViews[viewKey];
	}

	/**
	 * Determine if view has been designated as read-only in view configuration.
	 * @param {String} viewKey - key of view in form of "<View Id>|<Node Type>"
	 * @return {boolean} true if view is read-only
	 */
	function isViewReadOnly(viewKey) {
		return !!_readOnlyViews[viewKey];
	}

	return {
		addContextualPathHints,
		addHiddenPath,
		addPathHints,
		addAllPathHints,
		addUserDefault,
		applyUserDefaultsToQuery,
		createHiddenPathsForView,
		getAllContextualPathHintsIds,
		getAttributeNameAlongPath,
		getContextualPathHints,
		getCustomPathName,
		getPathHints,
		getPathName,
		getUserDefaultForAttribute,
		getUserDefaultsForType,
		init,
		isPathHidden,
		isPathNameExplicit,
		isSearchTypeHidden,
		isViewHidden,
		isViewReadOnly,
		removeSupersededUserDefaults,
		setUserDefaultValues
	};
});
